import React, { useState } from 'react';
import { Loader2, CreditCard } from 'lucide-react';
import { usePayment } from '../hooks/usePayment';
import { useAuth } from '../hooks/useAuth';
import { BillingInterval } from '../stripe-config';

interface PaymentButtonProps {
  interval: BillingInterval;
  buttonText?: string;
  disabled?: boolean;
  className?: string;
  priceId?: string; // override default price (e.g. Collaborator plan)
}

export function PaymentButton({
  interval,
  buttonText = `Subscribe ${interval === 'monthly' ? 'Monthly' : 'Annually'}`,
  disabled = false,
  className = '',
  priceId
}: PaymentButtonProps) {
  const { user } = useAuth();
  const { handlePayment, loading, error } = usePayment();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleClick = async () => {
    if (!user?.email) {
      console.error('User email not found');
      return;
    }

    try {
      setIsProcessing(true);

      const result = await handlePayment({
        interval,
        email: user.email,
        tier: interval === 'lifetime' ? 'lifetime' : (interval === 'annual' ? 'annual' : 'monthly'),
        priceId
      });

      if (result?.url) {
        // Redirect to Stripe checkout
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('Payment error:', err);
      // Error is handled by the usePayment hook
    } finally {
      setIsProcessing(false);
    }
  };

  const isLoading = loading || isProcessing;

  return (
    <div className="space-y-2">
      <button
        onClick={handleClick}
        disabled={disabled || isLoading}
        className={`w-full py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors ${
          className || 'bg-electric text-white hover:bg-electric/90 font-medium'
        }`}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4 mr-2" />
            {buttonText}
          </>
        )}
      </button>

      {error && (
        <p className="text-sm text-crimson">{error}</p>
      )}
    </div>
  );
}